import React, { useCallback, useEffect, useState } from 'react';

import { Box } from '@opendoor/bricks-next';
import Spritesheet from '@opendoor/bricks/core/Icon/Spritesheet';
import { ClientError } from 'graphql-request';

import { GetMarketplacePropertyQuery, PropertyDetailsFragment } from '__generated__/athena';

import { getAthenaClient } from 'components/api';
import {
  isBuyDirectEligible,
  usePropertyReactions,
  useSellerDisclosures,
  useSimilarProperties,
} from 'components/marketplace/helpers';
import useTrackProperty, {
  PropertyAnalytics,
} from 'components/marketplace/helpers/useTrackProperty';
import {
  GalleryPhotos,
  Lightbox,
  PhotoCarousel,
  PropertyDetailsType,
  PropertySection,
  ShareModal,
} from 'components/marketplace/property';
import MarketplaceLayout from 'components/properties/shared/MarketplaceLayout';

import {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';

import { generateDeploymentETag } from '../../../../helpers/cache';
import { useObservability } from '../../../../helpers/observability';

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const property = props?.property;
  return (
    <PropertyAnalytics.Provider value={{ property }}>
      <PageInner {...props} />
    </PropertyAnalytics.Provider>
  );
};

const PageInner: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  property,
}) => {
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const toggleLightbox = useCallback(() => setIsLightboxOpen((prev) => !prev), []);
  const handleSetSelectedPhotoIndex = useCallback((idx: number) => setSelectedPhotoIndex(idx), []);
  const { isLiked, onClickReaction } = usePropertyReactions(
    property.addressToken,
    'REACTION_SOURCE_WEB_PDP',
  );
  /**
   * The initial page load for customers will include the cached similar properties.
   * This hook executes client-side to retrieve the latest similar listings.
   */
  const marketplaceProperty: PropertyDetailsType = {
    ...property,
    isLiked,
    onClickReaction,
  };

  const { similarProperties } = useSimilarProperties(property.addressToken);
  if (similarProperties?.results) {
    marketplaceProperty.similarProperties = similarProperties;
  }

  const { sellerDisclosures } = useSellerDisclosures(property.addressToken);
  if (sellerDisclosures) {
    marketplaceProperty.sellerDisclosuresDownloadUrl = sellerDisclosures;
  }

  useEffect(() => {
    trackProperty('property-view', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: property.addressToken,
    });
  }, []);

  return (
    <MarketplaceLayout property={marketplaceProperty}>
      <Spritesheet />
      <Box
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        $smallerThanMD={{ flexDirection: 'column', overflowY: 'scroll' }}
        $largerThanSM={{ flexDirection: 'row', height: '100vh' }}
        overflow="hidden"
        width="100vw"
      >
        <GalleryPhotos
          handleSetSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          property={marketplaceProperty}
          toggleLightbox={toggleLightbox}
        />
        <PhotoCarousel
          photos={marketplaceProperty?.photos}
          price={
            marketplaceProperty?.displayBadge === 'ON_THE_MARKET' &&
            isBuyDirectEligible(marketplaceProperty)
              ? marketplaceProperty.buyDirectPrice
              : marketplaceProperty.listPrice
          }
          address={marketplaceProperty.street}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          latitude={marketplaceProperty.latitude}
          longitude={marketplaceProperty.longitude}
          openShareModal={() => setIsShareModalOpen(true)}
        />
        <Lightbox
          handleClose={toggleLightbox}
          isOpen={isLightboxOpen}
          property={marketplaceProperty}
          selectedPhotoIndex={selectedPhotoIndex}
        />
        <PropertySection property={marketplaceProperty} />
        <ShareModal
          property={marketplaceProperty}
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
        />
      </Box>
    </MarketplaceLayout>
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  property: PropertyDetailsFragment;
}> = async (context) => {
  // Set ETag header based on deployment version to prevent stale cached content.
  // This ensures Cloudflare will invalidate cache when new deployments are made.
  // The ETag changes with each deployment (via NEXT_PUBLIC_VERSION), causing Cloudflare
  // to treat cached responses as stale and fetch fresh content from the origin.
  const etag = generateDeploymentETag();
  context.res.setHeader('ETag', etag);

  const addressToken = context.query['address_id'];
  if (!addressToken || Array.isArray(addressToken)) {
    return {
      notFound: true,
    };
  }
  let propertyResult: GetMarketplacePropertyQuery | undefined;
  try {
    propertyResult = await getAthenaClient(context.req.headers).GetMarketplaceProperty({
      addressToken,
    });
  } catch (e) {
    if (e instanceof ClientError) {
      if (!e.response.data?.marketplace?.property) {
        return {
          notFound: true,
        };
      }
      // send to sentry
    } else {
      return {
        notFound: true,
      };
    }
  }

  const property = propertyResult?.marketplace?.property;

  if (!property) {
    return {
      notFound: true,
    };
  }

  // If the property is in an unknown state, we should not show it.
  // Applies to "takedown" requests and other edge cases
  if (property.listingState === 'UNKNOWN') {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      property,
    },
  };
};

export default Page;
